<script setup lang="ts">
import { useAuthStore } from '@stores/useAuthStore';
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import MediaImage from '@components/MediaImage.vue';
import Modal from '@components/Modal.vue';
import { useGalleryStore } from '@stores/useGalleryStore.ts';
import ContactOfferButton from '@components/ContactOfferButton.vue';
import { trans } from '@elements/translation';
import Typography from '@components/Typography.vue';
import Icon from '@components/Icon.vue';
import type { UUIDv4 } from '@global-types/uuid.ts';
import IconCaretLeft from '@icons/ph_caret-left.svg';
import IconCaretRight from '@icons/ph_caret-right.svg';
import IconChatsFill from '@icons/ph_chats-fill.svg';

interface GalleryProps {
  isOpen?: boolean,
  mediaUuids: UUIDv4[],
  hashId?: string | null,
  hasCtaLayer?: boolean,
  sellerOrganizationId?: number | null,
  proposalId?: number,
}

const {
  isOpen = false,
  mediaUuids,
  hashId = null,
  hasCtaLayer = false,
  sellerOrganizationId,
} = defineProps<GalleryProps>();

const emit = defineEmits([ 'close' ]);

const galleryStore = useGalleryStore();

const {
  setCurrentIndex,
  isNextIndex,
  makeUnscalable,
  makeScalable,
} = galleryStore;

const { currentIndex } = storeToRefs(galleryStore);
const ctaLayer = ref<boolean>(false);

const total = computed<number>(() => mediaUuids.length);
const showCtaLayer = computed<boolean>(() => hasCtaLayer && ctaLayer.value);

const ratio = (index: number): number[] | null => (index === 0 ? [ 3, 2 ] : null);

const previous = (): void => {
  if (ctaLayer.value) {
    ctaLayer.value = false;
    return;
  }

  const previousIndex = (currentIndex.value - 1);

  setCurrentIndex(previousIndex >= 0 ? previousIndex : (total.value + previousIndex));

  if (hasCtaLayer && currentIndex.value === total.value - 1) {
    ctaLayer.value = true;
  }
};

const next = (): void => {
  if (hasCtaLayer && currentIndex.value === total.value - 1 && !ctaLayer.value) {
    ctaLayer.value = true;
    return;
  }

  ctaLayer.value = false;

  setCurrentIndex((currentIndex.value + 1) % total.value);
};

const close = (): void => {
  makeUnscalable();

  setCurrentIndex(0);

  emit('close');
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (isOpen) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const actions: Record<string, () => void> = {
      ArrowLeft: previous,
      ArrowRight: next,
      Escape: close,
    };

    actions[event.key]?.();
  }
};

onMounted(() => {
  makeScalable();
  // Third argument to avoid flickity keydown event
  document.addEventListener('keydown', handleKeydown, true);
});

onUnmounted(() => {
  makeUnscalable();
  document.removeEventListener('keydown', handleKeydown, true);
});

watch(() => isOpen, (open) => {
  // eslint-disable-next-line no-unused-expressions
  open ? makeScalable() : makeUnscalable();
});

const bodyKey = computed<string>(() => `${hashId}_${isOpen ? '1' : '0'}`);

const contactHashIds = computed<string[]>(() => (hashId ? [ hashId ] : []));

const { user } = storeToRefs(useAuthStore());

const isOwner = computed<boolean>(() => !!sellerOrganizationId && (user.value?.organization?.id === sellerOrganizationId));
</script>

<template>
  <Modal
    v-if="isOpen"
    :is-opened="isOpen"
    :on-close="close"
    class="gallery-modal"
  >
    <template #header>
      <span class="gallery-modal__header">
        {{ trans('global.photo') }} {{ currentIndex + 1 }} / {{ total }}
      </span>
    </template>

    <template #body>
      <div class="gallery-modal__body-wrapper">
        <div
          :key="bodyKey"
          v-touch:swipe.left="next"
          v-touch:swipe.right="previous"
          class="gallery-modal__body"
        >
          <div class="gallery-modal__pictures">
            <MediaImage
              v-for="(uuid, index) in mediaUuids"
              v-show="index === currentIndex"
              :key="uuid"
              :img-class="`gallery-modal__img ${showCtaLayer ? 'gallery-modal__img--cta-layer' : ''}`"
              :uuid="uuid"
              :width="1000"
              :ratio="ratio(index) as number[] | undefined"
              :lazy="!isNextIndex(index)"
            />
          </div>

          <div class="gallery-modal__nav">
            <button
              class="gallery-modal__nav-button gallery-modal__nav-button--prev"
              :aria-label="trans('global.pagination.previous')"
              @click="previous"
            >
              <Icon
                :svg="IconCaretLeft"
                width="24"
                height="24"
                fill="black"
              />
            </button>

            <button
              class="gallery-modal__nav-button gallery-modal__nav-button--next"
              :aria-label="trans('global.pagination.next')"
              @click="next"
            >
              <Icon
                :svg="IconCaretRight"
                width="24"
                height="24"
                fill="black"
              />
            </button>
          </div>

          <div
            v-show="showCtaLayer"
            class="gallery-modal__ctas"
          >
            <Typography
              variant="content-heading-2"
              color="gray-darker"
            >
              {{ trans('offer.know_more.title') }}
            </Typography>

            <ContactOfferButton
              :hash-ids="contactHashIds"
              tracking-event="contact_offer_photos"
              :disabled="isOwner"
              :proposal-id="proposalId"
            >
              <Icon
                :svg="IconChatsFill"
                width="24"
                height="24"
                fill="white"
              />

              <span>
                {{ trans('offer.contact.actions.contact') }}
              </span>
            </ContactOfferButton>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.gallery-modal {
  z-index: 150;
  display: flex;
  justify-content: start;
  align-items: flex-start;

  &__body-wrapper {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    max-width: 100%;
    height: 90%;
    overflow: hidden;
  }

  &__img {
    max-width: 100%;
    max-height: 100%;
  }

  &__pictures {
    max-width: 100%;
    max-height: 100%;
  }

  &__nav {
    position: absolute;
    width: 100%;
    justify-content: space-between;
    display: flex;
    padding: toRem(8);
  }

  &__nav-button {
    display: block;
    width: toRem(40);
    height: toRem(40);
    border: 1px solid $colorGray;
    border-radius: toRem(4);
    background-color: $colorWhite;
    z-index: 2;
    padding: toRem(8);
  }

  &__ctas {
    position: absolute;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: toRem(4);
    min-width: toRem(160);
  }
}

:deep(.modal-title) {
  @apply p-4;
}

:deep(.modal-container) {
  @apply relative flex-1 h-full;
}

:deep(.gallery-modal__img) {
  @apply max-w-full max-h-full;
}

:deep(.gallery-modal__img--cta-layer) {
  @apply opacity-30;
}

@media screen and (min-width: $breakTablet) {
  .gallery-modal {
    &__ctas {
      gap: toRem(8);
    }
  }
}
</style>
